import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg, eur, todayRome } from "@/lib/api";
import { LISTINO, SPECIES, SERVICES, tierForWeight } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, NativeSelect } from "@/components/common";

export const EMPTY_PRATICA = {
  tipo_cliente: "privato", vet_id: "", nome: "", telefono: "", email: "", indirizzo: "", citta: "", codice_fiscale: "",
  animale_nome: "", specie: "Cane", razza: "", peso_kg: "", microchip: "", servizio: "singola", data_ritiro: "",
  imponibile: 0, iva: 22, acconto: 0, acconto_data: "", saldo: 0, saldo_data: "", booking_id: null, provenienza: "", note: "", fatturato: true, fonte: "google",
};

const PROVENIENZE = { "": "—", google: "Google", pet_heaven: "Pet Heaven", veterinario: "Veterinario", cliente: "Cliente / passaparola" };

export const praticaFromBooking = (b) => {
  const tier = tierForWeight(b.weight_kg);
  return {
    ...EMPTY_PRATICA, tipo_cliente: b.vet_id ? "veterinario" : "privato", vet_id: b.vet_id || "",
    nome: b.owner_name || "", telefono: b.owner_phone || "", email: b.owner_email || "", indirizzo: b.pickup_address || "",
    citta: b.city || "", animale_nome: b.pet_name || "", specie: b.species || "Cane", peso_kg: b.weight_kg ?? "",
    servizio: b.service_type || "singola", data_ritiro: b.date, booking_id: b.id,
    imponibile: b.service_type === "singola" && tier?.price ? tier.price : 0,
  };
};

const Section = ({ title, children }) => (
  <div>
    <p className="font-display text-lg font-semibold text-[#0D3B52] mb-3 border-b border-[#E2DDD5] pb-1">{title}</p>
    <div className="grid sm:grid-cols-3 gap-4">{children}</div>
  </div>
);

export const PraticaDialog = ({ open, onOpenChange, initial, vets, onSaved }) => {
  const [f, setF] = useState(EMPTY_PRATICA);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...EMPTY_PRATICA, ...(initial || {}), vet_id: initial?.vet_id || "" }); }, [open, initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const num = (v) => (v === "" || v == null ? 0 : Number(v));
  const totale = num(f.imponibile) * (1 + num(f.iva) / 100);
  const residuo = totale - num(f.acconto) - num(f.saldo);

  const applyTier = (e) => {
    const t = LISTINO.find((x) => x.id === e.target.value);
    if (t?.price) setF((p) => ({ ...p, imponibile: t.price }));
  };

  const save = async () => {
    if (!f.nome.trim()) return toast.error("Inserisci il nome del cliente");
    setBusy(true);
    const payload = {
      ...f, vet_id: f.vet_id || null, peso_kg: f.peso_kg === "" ? null : Number(f.peso_kg),
      imponibile: num(f.imponibile), iva: num(f.iva), acconto: num(f.acconto), saldo: num(f.saldo),
    };
    try {
      const { data } = f.id ? await api.put(`/admin/pratiche/${f.id}`, payload) : await api.post("/admin/pratiche", payload);
      toast.success(data.cliente_account?.password ? `Anagrafica salvata · Accesso cliente creato: ${data.cliente_account.login} / ${data.cliente_account.password}` : "Anagrafica salvata", { duration: data.cliente_account?.password ? 12000 : 4000 });
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#FAF8F5]" data-testid="pratica-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-[#0D3B52]">{f.id ? "Modifica anagrafica" : "Nuova anagrafica cliente"}</DialogTitle>
          <DialogDescription>Dati del cliente, dell'animale, del servizio e dei pagamenti (acconto / saldo).</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <Section title="Cliente">
            <Field label="Tipo"><NativeSelect data-testid="pratica-tipo" value={f.tipo_cliente} onChange={set("tipo_cliente")}><option value="privato">Privato</option><option value="veterinario">Tramite veterinario</option></NativeSelect></Field>
            <Field label="Nome e cognome *" className="sm:col-span-2"><Input data-testid="pratica-nome" value={f.nome} onChange={set("nome")} className="bg-white" /></Field>
            <Field label="Telefono"><Input data-testid="pratica-telefono" value={f.telefono} onChange={set("telefono")} className="bg-white" /></Field>
            <Field label="Email"><Input data-testid="pratica-email" value={f.email} onChange={set("email")} className="bg-white" /></Field>
            <Field label="Codice fiscale"><Input data-testid="pratica-cf" value={f.codice_fiscale} onChange={set("codice_fiscale")} className="bg-white" /></Field>
            <Field label="Indirizzo di residenza" className="sm:col-span-2"><Input data-testid="pratica-indirizzo" value={f.indirizzo} onChange={set("indirizzo")} className="bg-white" /></Field>
            <Field label="Città"><Input data-testid="pratica-citta" value={f.citta} onChange={set("citta")} className="bg-white" /></Field>
            <Field label="Veterinario collegato" className="sm:col-span-3">
              <NativeSelect data-testid="pratica-vet" value={f.vet_id} onChange={set("vet_id")}>
                <option value="">— Nessuno —</option>
                {vets.map((v) => <option key={v.id} value={v.id}>{v.clinic_name}</option>)}
              </NativeSelect>
            </Field>
          </Section>
          <Section title="Animale e servizio">
            <Field label="Nome animale"><Input data-testid="pratica-animale" value={f.animale_nome} onChange={set("animale_nome")} className="bg-white" /></Field>
            <Field label="Specie"><NativeSelect data-testid="pratica-specie" value={f.specie} onChange={set("specie")}>{SPECIES.map((s) => <option key={s}>{s}</option>)}</NativeSelect></Field>
            <Field label="Razza"><Input value={f.razza} onChange={set("razza")} className="bg-white" /></Field>
            <Field label="Peso (kg)"><Input data-testid="pratica-peso" type="number" value={f.peso_kg} onChange={set("peso_kg")} className="bg-white" /></Field>
            <Field label="N° microchip"><Input data-testid="pratica-microchip" value={f.microchip} onChange={set("microchip")} className="bg-white" /></Field>
            <Field label="Data ritiro"><Input data-testid="pratica-data-ritiro" type="date" value={f.data_ritiro} onChange={set("data_ritiro")} className="bg-white" /></Field>
            <Field label="Servizio"><NativeSelect data-testid="pratica-servizio" value={f.servizio} onChange={set("servizio")}>{Object.entries(SERVICES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></Field>
            <Field label="Provenienza"><NativeSelect data-testid="pratica-provenienza" value={f.provenienza || ""} onChange={set("provenienza")}>{Object.entries(PROVENIENZE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></Field>
            <Field label="Applica listino" className="sm:col-span-2">
              <NativeSelect data-testid="pratica-listino" defaultValue="" onChange={applyTier}>
                <option value="">Scegli taglia…</option>
                {LISTINO.filter((t) => t.price).map((t) => <option key={t.id} value={t.id}>{t.label} ({t.range}) – {eur(t.price)} + IVA</option>)}
              </NativeSelect>
            </Field>
          </Section>
          <Section title="Importi e pagamenti">
            <Field label="Imponibile €"><Input data-testid="pratica-imponibile" type="number" step="0.01" value={f.imponibile} onChange={set("imponibile")} className="bg-white" /></Field>
            <Field label={f.fatturato === false ? "IVA % (non ivato)" : "IVA %"}><Input data-testid="pratica-iva" type="number" value={f.fatturato === false ? 0 : f.iva} disabled={f.fatturato === false} onChange={set("iva")} className="bg-white" /></Field>
            <Field label="Prezzo (uso interno, non visibile al cliente)">
              <NativeSelect data-testid="pratica-fatturato" value={f.fatturato === false ? "no" : "si"} onChange={(e) => setF((p) => ({ ...p, fatturato: e.target.value === "si", iva: e.target.value === "si" ? (Number(p.iva) || 22) : 0 }))}>
                <option value="si">Ivato / fatturato</option><option value="no">Non ivato / non fatturato</option>
              </NativeSelect>
            </Field>
            <Field label="Fonte cliente">
              <NativeSelect data-testid="pratica-fonte" value={f.fonte || "google"} onChange={set("fonte")}>
                <option value="google">Google</option><option value="pet">Da Pet (agenzia)</option><option value="veterinario">Veterinario</option><option value="cliente">Già cliente</option><option value="altro">Altro</option>
              </NativeSelect>
            </Field>
            <Field label={f.fatturato === false ? "Totale (non ivato)" : "Totale (IVA incl.)"}><div className="h-10 flex items-center font-display text-2xl font-semibold text-[#0D3B52]" data-testid="pratica-totale">{eur(totale)}</div></Field>
            <Field label="Acconto €"><Input data-testid="pratica-acconto" type="number" step="0.01" value={f.acconto} onChange={set("acconto")} className="bg-white" /></Field>
            <Field label="Data acconto"><Input data-testid="pratica-acconto-data" type="date" value={f.acconto_data} onChange={set("acconto_data")} className="bg-white" /></Field>
            <div className="hidden sm:block" />
            <Field label="Saldo €"><Input data-testid="pratica-saldo" type="number" step="0.01" value={f.saldo} onChange={set("saldo")} className="bg-white" /></Field>
            <Field label="Data saldo"><Input data-testid="pratica-saldo-data" type="date" value={f.saldo_data} onChange={set("saldo_data")} className="bg-white" /></Field>
            <Field label="Residuo"><div className={`h-10 flex items-center font-semibold ${residuo > 0.009 ? "text-rose-700" : "text-emerald-700"}`} data-testid="pratica-residuo">{eur(residuo)}</div></Field>
          </Section>
          <Field label="Note"><Textarea value={f.note} onChange={set("note")} className="bg-white" rows={2} /></Field>
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setF((p) => ({ ...p, saldo: Math.max(0, +(totale - num(p.acconto)).toFixed(2)), saldo_data: p.saldo_data || todayRome() }))} data-testid="pratica-salda-button">Segna come saldato</Button>
            <Button onClick={save} disabled={busy} data-testid="pratica-save-button" className="rounded-full bg-[#0D3B52] hover:bg-[#082535] px-8">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
